import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import theme from '../../theme';

interface WorkshopData {
  underMaintenance: number;
  accidentRepair: number;
  underMaintenanceAlQouz: number;
  underMaintenanceAgency: number;
  others: number;
  total: number;
}

interface WorkshopBreakdownProps {
  data: WorkshopData;
}

export const WorkshopBreakdown: React.FC<WorkshopBreakdownProps> = ({ data }) => {
  const maxValue = Math.max(
    data.underMaintenance,
    data.accidentRepair,
    data.underMaintenanceAlQouz,
    data.underMaintenanceAgency,
    data.others,
    1
  );
  
  const getBarWidth = (value: number): number => {
    return (value / maxValue) * 100;
  };

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <Ionicons name="construct" size={24} color={theme.colors.orange600} />
        <Text style={styles.title}>Workshop Breakdown</Text>
      </View>
      
      <View style={styles.totalCard}>
        <Text style={styles.totalLabel}>Total in Workshop</Text>
        <Text style={styles.totalValue}>{data.total}</Text>
      </View>

      <View style={styles.barsContainer}>
        {/* Under Maintenance Bar */}
        <View style={styles.barRow}>
          <View style={styles.barLabel}>
            <Ionicons name="construct-outline" size={16} color={theme.colors.blue600} />
            <Text style={styles.labelText}>Under Maintenance</Text>
          </View>
          <View style={styles.barContainer}>
            <View 
              style={[
                styles.bar, 
                { 
                  width: `${getBarWidth(data.underMaintenance)}%`,
                  backgroundColor: theme.colors.blue600 
                }
              ]} 
            />
          </View>
          <Text style={styles.valueText}>{data.underMaintenance}</Text>
        </View>

        {/* Accident Repair Bar */}
        <View style={styles.barRow}>
          <View style={styles.barLabel}>
            <Ionicons name="alert-circle-outline" size={16} color={theme.colors.red600} />
            <Text style={styles.labelText}>Accident Repair</Text>
          </View>
          <View style={styles.barContainer}>
            <View 
              style={[
                styles.bar, 
                { 
                  width: `${getBarWidth(data.accidentRepair)}%`,
                  backgroundColor: theme.colors.red600 
                }
              ]} 
            />
          </View>
          <Text style={styles.valueText}>{data.accidentRepair}</Text>
        </View>

        {/* Under Maintenance Al Qouz Bar */}
        <View style={styles.barRow}>
          <View style={styles.barLabel}>
            <Ionicons name="build-outline" size={16} color={theme.colors.purple600} />
            <Text style={styles.labelText}>Maintenance Al Qouz</Text>
          </View>
          <View style={styles.barContainer}>
            <View 
              style={[
                styles.bar, 
                { 
                  width: `${getBarWidth(data.underMaintenanceAlQouz)}%`,
                  backgroundColor: theme.colors.purple600 
                }
              ]} 
            />
          </View>
          <Text style={styles.valueText}>{data.underMaintenanceAlQouz}</Text>
        </View>

        {/* Under Maintenance In Agency Bar */}
        <View style={styles.barRow}>
          <View style={styles.barLabel}>
            <Ionicons name="business-outline" size={16} color={theme.colors.green600} />
            <Text style={styles.labelText}>Maintenance In Agency</Text>
          </View>
          <View style={styles.barContainer}>
            <View 
              style={[
                styles.bar, 
                { 
                  width: `${getBarWidth(data.underMaintenanceAgency)}%`,
                  backgroundColor: theme.colors.green600 
                }
              ]} 
            />
          </View>
          <Text style={styles.valueText}>{data.underMaintenanceAgency}</Text>
        </View>

        {/* Others Bar */}
        <View style={styles.barRow}>
          <View style={styles.barLabel}>
            <Ionicons name="ellipsis-horizontal-circle-outline" size={16} color={theme.colors.gray500} />
            <Text style={styles.labelText}>Others</Text>
          </View>
          <View style={styles.barContainer}>
            <View 
              style={[
                styles.bar, 
                { 
                  width: `${getBarWidth(data.others)}%`,
                  backgroundColor: theme.colors.gray500 
                }
              ]} 
            />
          </View>
          <Text style={styles.valueText}>{data.others}</Text>
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: theme.colors.white,
    borderRadius: theme.borderRadius.lg,
    padding: theme.spacing.md,
    ...theme.shadows.md,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: theme.spacing.md,
  },
  title: {
    fontSize: theme.typography.sizes.lg,
    fontWeight: '700' as '700',
    color: theme.colors.textPrimary,
    marginLeft: theme.spacing.sm,
  },
  totalCard: {
    backgroundColor: theme.colors.gray50,
    borderRadius: theme.borderRadius.md,
    padding: theme.spacing.md,
    marginBottom: theme.spacing.md,
    alignItems: 'center',
  },
  totalLabel: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textSecondary,
    marginBottom: 4,
  },
  totalValue: {
    fontSize: theme.typography.sizes.xxxl,
    fontWeight: '700' as '700',
    color: theme.colors.orange600,
  },
  barsContainer: {
    marginTop: theme.spacing.sm,
  },
  barRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: theme.spacing.md,
  },
  barLabel: {
    flexDirection: 'row',
    alignItems: 'center',
    width: 200,
  },
  labelText: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textPrimary,
    marginLeft: theme.spacing.xs,
  },
  barContainer: {
    flex: 1,
    height: 24,
    backgroundColor: theme.colors.gray100,
    borderRadius: theme.borderRadius.sm,
    overflow: 'hidden',
    marginHorizontal: theme.spacing.sm,
  },
  bar: {
    height: '100%',
    borderRadius: theme.borderRadius.sm,
    minWidth: 2,
  },
  valueText: {
    fontSize: theme.typography.sizes.md,
    fontWeight: '700' as '700',
    color: theme.colors.textPrimary,
    width: 40,
    textAlign: 'right',
  },
});
